import { DecimalPipe } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter, HostListener, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { mongoId, round } from '../../../../../@core/common/global';
import { ApiService } from '../../../../../@core/service/api.service';
import { SharedDataService } from '../../../../../@core/service/shared-data.service';
import { CompilationModel } from '../../../../../@models/erp/compilation';
import { CompilationTypeData, DocConfig, IssuerData, UnitData } from '../../../../../@models/erp/compilationData';
import { EProductType, FinishedProductModel } from '../../../../../@models/erp/finishedProduct';
import { LineItemModel } from '../../../../../@models/erp/lineItem';
import { EAccountingType, PriceItem, PriceMatrixModel } from '../../../../../@models/erp/priceMatrix';
import { RoundPipe } from '../../../../../@theme/pipes';
import { NbPopoverDirective, NbToastrService } from '@nebular/theme';
import { FileModel } from '../../../../../@models';

export class DocMetrics {
  isEnergy = false;

  basicUnits: {
    qm: number;
    qmlm: number;
    qmkg: number;
  } = { qm: 0, qmlm: 0, qmkg: 0 };

  rollDimensions: {
    m2roll: number;
    lmroll: number;
    kgroll: number;
    outerDiameter: number;
  } = { m2roll: 0, lmroll: 0, kgroll: 0, outerDiameter: 0};

  boxInfo: {
    fpd: string[];
    type: string[];
    rollsPerBox: number[];
    status: string[];
    numBoxes: number[];
    fullUp: number[];
    fullUpUnit: number[];
    fullDown: number[];
    fullDownUnit: number[];
    maxBoxes: number[];
    numPallets: number[];
    palletStatus: string[];
    fullPalletUp: number[];
    fullPalletUpUnit: number[];
    fullPalletDown: number[];
    fullPalletDownUnit: number[];
  } = { 
    fpd: [], 
    type: [], 
    rollsPerBox: [], 
    status: [], 
    numBoxes: [], 
    fullUp: [], 
    fullUpUnit: [], 
    fullDown: [], 
    fullDownUnit: [],
    maxBoxes: [],
    numPallets: [],
    palletStatus: [],
    fullPalletUp: [],
    fullPalletUpUnit: [],
    fullPalletDown: [],
    fullPalletDownUnit: [],
  };

  calcValues: {
    quantity: number;
    quantityUnit: number;
    price: number;
    priceUnit: number;
    priceDate: Date;
    secondUnit: number;
    turnover: number;
  } = { quantity: null, quantityUnit: 1, price: null, priceUnit: 1, priceDate: new Date(), secondUnit: 0, turnover: 0 };

  metrics = new class {
    rollLength: number;
    rollWidth: number;
    areaWeight: number;

    pc: number = null;
    m: number = null;
    m100: number = null;
    m2: number = null;
    kg: number = null;

    pricePc: number = null;
    priceM: number = null;
    priceM100: number = null;
    priceM2: number = null;
    priceKg: number = null;

    matrixPiecePrice: number = null;
    pmFromDelivery = false;
    pmFromDispatch = false;

    calcValues: any = null;

    init = (rl: number, rw: number, aw: number, calcValues: any) => {
      this.rollLength = rl; this.rollWidth = rw; this.areaWeight = aw; this.calcValues = calcValues;
    }

    convert(value, fromUnit, toUnit) {
      let pc = 0;
      switch (fromUnit) {
        case 1: pc = value; break; // pieces
        case 2: pc = value  / this.rollLength; break; // linearmeters
        case 3: pc = 100 * value / this.rollLength; break; // 100 linearmeters
        case 4: pc = 1000 * value / (this.rollWidth * this.rollLength); break; // squaremeters
        case 5: pc = 1000000 * value / (this.rollWidth * this.rollLength * this.areaWeight); break; // kilogram
      }
      switch (toUnit) {
        case 1: return pc;
        case 2: return pc * this.rollLength;
        case 3: return pc * this.rollLength * 0.01;
        case 4: return pc * this.rollLength * this.rollWidth / 1000;
        case 5: return pc * this.rollLength * this.rollWidth * this.areaWeight / 1000000;
      }
    }
  }

  currentPriceMatrix: PriceItem;
  fullMatrix: PriceMatrixModel;

  constructor(
    private api: ApiService,
    private compilation: CompilationModel,
    private lineItem: LineItemModel,
    private roundPipe: RoundPipe,
    private data: SharedDataService,
    private doInit: boolean = true,
  ) {
    this.compilation = compilation;
    // console.log('line item, this.lineItem', this.lineItem.pfspo?.map(m => m.code), lineItem.pfspo?.map(m => m.code))
    this.lineItem = lineItem;
    if (doInit) this.init();
  }

  async init() {
    const finishedProduct = this.lineItem.finishedProduct;
    if (!finishedProduct) return;
    this.metrics.init (
      finishedProduct && finishedProduct.content && finishedProduct.content.energy ?
        finishedProduct.content.energy.rollLength : null,
      finishedProduct && finishedProduct.content && finishedProduct.content.energy ?
        finishedProduct.content.energy.width : null,
      finishedProduct && finishedProduct.product ?
        finishedProduct.product.weight : null,
      this.calcValues
    )

    this.boxInfo.type = finishedProduct.cartonSize;
    this.boxInfo.rollsPerBox = finishedProduct.piecesPerCarton;
    this.boxInfo.fpd = finishedProduct.packingSpec;

    this.currentPriceMatrix = await this.loadPrice();

    this.isEnergy = +finishedProduct.type === EProductType.Energy;

    // console.log('line item quantity', this.lineItem.quantity, this.lineItem.displayUnitQuantity, this.lineItem.pfspo?.map(m => m.code));

    if (!this.isEnergy) {
      this.calcValues.quantity = this.lineItem.quantity > 0 ? +this.lineItem.quantity : null;
      this.calcValues.price = +this.lineItem.piecePrice;
      await this.setPrice();
      this.calc();
      return;
    }

    this.data.loadCores();

    this.basicUnits.qm = 1;
    this.basicUnits.qmlm = this.basicUnits.qm / (this.metrics.rollWidth / 1000);
    this.basicUnits.qmkg = this.basicUnits.qm * this.metrics.areaWeight / 1000;

    this.rollDimensions.m2roll = this.metrics.rollLength / this.basicUnits.qmlm;
    this.rollDimensions.lmroll = this.metrics.rollLength;
    this.rollDimensions.kgroll = this.rollDimensions.m2roll * this.metrics.areaWeight / 1000;

    const tapeThickness = +finishedProduct.product.thickness;
    const core = this.data.cores.find(f => mongoId(f) === mongoId(finishedProduct.content.energy.core))
    const coreOuter = +core?.outerDiameter;
    this.rollDimensions.outerDiameter = coreOuter && !core.name.toUpperCase()?.startsWith('D-') && Math.sqrt(((this.metrics.rollLength * 1000 * 4 * tapeThickness / 1000 / Math.PI + coreOuter*coreOuter)));
    // console.log('outer diameter', tapeThickness, coreOuter, finishedProduct);

    this.calcValues.quantity = this.lineItem.quantity > 0 ? +this.lineItem.displayUnitQuantity : null;
    this.calcValues.quantityUnit = +this.lineItem.displayUnit;
    await this.setPrice();
    this.calcValues.secondUnit = +this.calcValues.quantityUnit;
    this.calc();
    // console.log('line item quantity3', this.lineItem.quantity, this.lineItem.displayUnitQuantity);

    const doctype = CompilationTypeData.get(this.compilation.type);
    const pricetype = DocConfig.get('prices').docs.indexOf(doctype.code);
    const acc = DocConfig.get('prices').price[pricetype] === 'PP' ? false: true; // EAccountingType.Purchase : EAccountingType.Sale;
    this.fullMatrix = await this.api.loadPrices(this.compilation.issuer, mongoId(this.compilation.recipient), mongoId(finishedProduct), acc);
    // console.log('full matrix', mongoId(finishedProduct), this.compilation.issuer, this.compilation.recipient, doctype, pricetype, acc, this.fullMatrix);
  }

  async setPrice(update: boolean = false) {
    if (!update && (this.lineItem.piecePrice || this.lineItem.quantity)) {
      // const price = this.decimalPipe.transform(this.lineItem.displayUnitPrice, '1.2-3');
      // this.calcValues.price = +price;
      // this.calcValues.priceUnit = this.lineItem.displayUnit || 1; // default pieces
      if (!+this.lineItem.metricsUnit) this.lineItem.metricsUnit = +this.lineItem.displayUnit;
      this.calcValues.priceUnit = +this.lineItem.metricsUnit;
      this.calcValues.price = +this.lineItem.metricsPrice ?
        +this.lineItem.metricsPrice : this.metrics.convert(this.lineItem.piecePrice, this.lineItem.metricsUnit, 1);
      this.calcValues.quantity = this.lineItem.displayUnitQuantity;
      this.calcValues.quantityUnit = +this.lineItem.displayUnit;
    } else {
      const price = await this.loadPrice();
      // console.log('set price', price);
      this.currentPriceMatrix = price;
      this.calcValues.price = price && price.value;
      this.calcValues.priceUnit = (price && price.type) || 1; // default pieces
      if (this.lineItem.displayUnitQuantity) this.calcValues.quantity = this.lineItem.displayUnitQuantity;
      if (this.lineItem.displayUnit) this.calcValues.quantityUnit = +this.lineItem.displayUnit;
    }
  }

  async loadPrice() {

    // TB#1053(1)
    if (DocConfig.get('pricesFromDeliveryDate').docs.includes(CompilationTypeData.get(this.compilation.type).code)) {
      if (this.lineItem.dateOfDelivery) this.calcValues.priceDate = this.lineItem.dateOfDelivery;
      this.metrics.pmFromDelivery = true;
    } else {
      if (this.compilation.dateOfDispatch) this.calcValues.priceDate = this.compilation.vieDispatchDate;
      this.metrics.pmFromDispatch = true
    }

    const priceMatrixItem = this.compilation.recipient && this.lineItem.finishedProduct && await this.api.loadPrice(
      this.compilation.issuer,
      mongoId(this.compilation.recipient),
      mongoId(this.lineItem.finishedProduct),
      this.calcValues.priceDate,
      this.compilation.currency,
      DocConfig.isSalePrice(+this.compilation.type)
    );

    // console.log('load price', this.calcValues.priceDate, mongoId(this.compilation.recipient), mongoId(this.lineItem.finishedProduct), priceMatrixItem);

    return priceMatrixItem;
  }

  // updateMatrixPiecePrice() {
  //   if (this.currentPriceMatrix) {
  //     this.metrics.matrixPiecePrice
  //       = round(this.currentPriceMatrix.value / this.metrics.convert(1, +this.currentPriceMatrix.type, 1), 6);
  //     console.log('matrix piece price', this.currentPriceMatrix, this.metrics.matrixPiecePrice);
  //   }
  // }

  getTurnover(price: number, unit: number) {
    let total = 0;
    switch (unit) {
      case 1: total = this.metrics.pc * price; break
      case 2: total = this.metrics.m * price; break
      case 3: total = this.metrics.m100 * price; break
      case 4: total = this.metrics.m2 * price; break
      case 5: total = this.metrics.kg * price; break
    }
    return total;
  }

  getPiecePrice(price: number, unit: number) {
    return this.getTurnover(price, unit) / this.metrics.pc;
  }

  calc(unitChanged = false) {
    const quantity = +this.calcValues.quantity;
    const quantityUnit = +this.calcValues.quantityUnit;
    const price = +this.calcValues.price;
    const priceUnit = +this.calcValues.priceUnit;

    if (!this.isEnergy) {
      this.calcValues.turnover = quantity * price;
      this.metrics.pc = this.calcValues.quantity;
      this.metrics.pricePc = this.calcValues.price;
      this.calcValues.quantityUnit = this.calcValues.secondUnit = 1;
      return;
    }

    this.metrics.pc = this.metrics.convert(quantity, quantityUnit, 1);
    this.metrics.m = this.metrics.convert(quantity, quantityUnit, 2);
    this.metrics.m100 = this.metrics.convert(quantity, quantityUnit, 3);
    this.metrics.m2 = this.metrics.convert(quantity, quantityUnit, 4);
    this.metrics.kg = this.metrics.convert(quantity, quantityUnit, 5);

    this.calcValues.turnover = this.getTurnover(price, priceUnit);

    this.metrics.pricePc = this.calcValues.turnover / this.metrics.pc;
    this.metrics.priceM = this.calcValues.turnover / this.metrics.m;
    this.metrics.priceM100 = this.calcValues.turnover / this.metrics.m100;
    this.metrics.priceM2 = this.calcValues.turnover / this.metrics.m2;
    this.metrics.priceKg = this.calcValues.turnover / this.metrics.kg;

    // this.updateMatrixPiecePrice()

    let unitPrice = 0;
    switch (quantityUnit) {
      case 1: unitPrice = this.metrics.pricePc; break;
      case 2: unitPrice = this.metrics.priceM; break;
      case 3: unitPrice = this.metrics.priceM100; break;
      case 4: unitPrice = this.metrics.priceM2; break;
      case 5: unitPrice = this.metrics.priceKg; break;
    }

    if (unitChanged) {
      this.calcValues.secondUnit = quantityUnit;
    }

    // const totalQuantity = this.lineItem.quantity;
    // const boxRatio = totalQuantity / this.boxInfo.rollsPerBox;
    // this.boxInfo.fullUp = this.boxInfo.rollsPerBox * Math.ceil(boxRatio);
    // this.boxInfo.fullUpUnit =  Math.round(this.metrics.pricePc * this.boxInfo.fullUp / unitPrice * 100) / 100;
    // this.boxInfo.fullDown = this.boxInfo.rollsPerBox * Math.floor(boxRatio);
    // this.boxInfo.fullDownUnit =  Math.round(this.metrics.pricePc * this.boxInfo.fullDown / unitPrice * 100) / 100;
    // const diff1 = this.boxInfo.fullUp - totalQuantity;
    // const diff2 = this.boxInfo.fullDown - totalQuantity;
    // this.boxInfo.status = Number.isInteger(boxRatio) ? 'full': ''+(Math.round((Math.abs(diff1) < Math.abs(diff2) ? diff1 : diff2) * 100) / 100);

    const totalQuantity = this.metrics.pc;
    const boxRatio = this.boxInfo.rollsPerBox.map(m => totalQuantity / m);
    // num boxes real
    // const numBoxes = this.boxInfo.type.map(m => this.compilation.packingConditions.flatMap(m => m.items)?.filter(f => f.typeOfBox === m)?.length || 0);
    this.boxInfo.numBoxes = boxRatio.map(m => Math.ceil(m)); // numBoxes;
    this.boxInfo.fullUp = this.boxInfo.rollsPerBox.map((m, idx) => m * Math.ceil(boxRatio[idx]));
    this.boxInfo.fullUpUnit =  this.boxInfo.fullUp.map(m => Math.round(this.metrics.pricePc * m / unitPrice * 100) / 100);
    this.boxInfo.fullDown = this.boxInfo.rollsPerBox.map((m, idx) => m * Math.floor(boxRatio[idx]));
    this.boxInfo.fullDownUnit =  this.boxInfo.fullDown.map(m => Math.round(this.metrics.pricePc * m / unitPrice * 100) / 100);
    const diff1 = this.boxInfo.fullUp.map(m => m  - totalQuantity);
    const diff2 = this.boxInfo.fullDown.map(m => m - totalQuantity);
    this.boxInfo.status = diff1.map((d1, idx) => Number.isInteger(boxRatio[idx]) ? 'full': ''+(Math.round((Math.abs(d1) < Math.abs(diff2[idx]) ? d1 : diff2[idx]) * 100) / 100));

    const fpd = this.boxInfo.fpd?.map(m => this.data.getPackingConfig(m));
    if (fpd?.length) {
      this.boxInfo.maxBoxes = fpd.map(m => m.boxesPerLayer * m.layersPerPallet);
      const palletQ = this.boxInfo.rollsPerBox.map((m, idx) => m * this.boxInfo.maxBoxes[idx]);
      const palletRatio = palletQ.map(m => totalQuantity / m);
      // num pallets real
      // const numPallets = fpd.map(m => this.compilation.packingConditions.filter(f => f.packingType === m.id)?.length || 0);
      this.boxInfo.numPallets = palletRatio.map(m => Math.ceil(m)); // numPallets;
      this.boxInfo.fullPalletUp = palletQ.map((m, idx) => m * Math.ceil(palletRatio[idx]));
      this.boxInfo.fullPalletUpUnit =  this.boxInfo.fullPalletUp.map(m => Math.round(this.metrics.pricePc * m / unitPrice * 100) / 100);
      this.boxInfo.fullPalletDown = palletQ.map((m, idx) => m * Math.floor(palletRatio[idx]));
      this.boxInfo.fullPalletDownUnit =  this.boxInfo.fullPalletDown.map(m => Math.round(this.metrics.pricePc * m / unitPrice * 100) / 100);
      const diff1 = this.boxInfo.fullPalletUp.map(m => m  - totalQuantity);
      const diff2 = this.boxInfo.fullPalletDown.map(m => m - totalQuantity);
      this.boxInfo.palletStatus = diff1.map((d1, idx) => Number.isInteger(palletRatio[idx]) ? 'full': ''+(Math.round((Math.abs(d1) < Math.abs(diff2[idx]) ? d1 : diff2[idx]) * 100) / 100));

      // console.log('box info', palletQ, palletRatio, numPallets, this.boxInfo, this.data);

    }

  }

  save() {
    let quantity = +this.calcValues.quantity;
    const unit = +this.calcValues.secondUnit;
    switch (unit) {
      case 1: quantity = this.metrics.pc; break;
      case 2: quantity = this.metrics.m; break;
      case 3: quantity = this.metrics.m100; break;
      case 4: quantity = this.metrics.m2; break;
      case 5: quantity = this.metrics.kg; break;
    }
    // this.lineItem.displayUnitQuantity = +quantity;
    // this.lineItem.displayUnit = unit > 0 ? unit : +this.calcValues.quantityUnit;
    // this.lineItem.displayUnitPrice = +quantity !== 0 ? +this.calcValues.turnover / +quantity : 0;
    // this.lineItem.quantity = +this.metrics.pc;
    // this.lineItem.piecePrice = +this.metrics.pricePc || 0;
    // this.lineItem.metricsUnit = +this.calcValues.priceUnit;
    // this.lineItem.metricsPrice = +this.calcValues.price || 0;
    this.lineItem.displayUnitQuantity = +quantity;
    this.lineItem.displayUnit = unit > 0 ? unit : +this.calcValues.quantityUnit;
    this.lineItem.quantity = +this.metrics.pc;
    this.lineItem.calculatedPiecePrice = +this.metrics.pricePc || 0;
    this.lineItem.piecePrice = +this.roundPipe.transform(this.lineItem.calculatedPiecePrice, 3);
    this.lineItem.displayUnitPrice = +quantity !== 0 ? this.lineItem.piecePrice * this.metrics.pc / +quantity : 0;
    this.lineItem.metricsUnit = +this.calcValues.priceUnit;
    this.lineItem.metricsPrice = +this.calcValues.price || 0;
    // console.log('save price', this.lineItem.calculatedPiecePrice, this.roundPipe.transform(this.lineItem.calculatedPiecePrice, 3), this.lineItem.piecePrice, this.metrics, this.calcValues, this.lineItem);
  }
}

@Component({
  selector: 'ngx-line-item-metrics',
  templateUrl: './line-item-metrics.component.html',
  styleUrls: ['./line-item-metrics.component.scss'],
})
export class LineItemMetricsComponent implements OnInit {
  @Input() compilation: CompilationModel;
  @Input() lineItem: LineItemModel;
  @Input() disabled: boolean = false;
  @Output() recalc: EventEmitter<void> = new EventEmitter<void>();
  @Output() close: EventEmitter<void> = new EventEmitter<void>();

  @ViewChildren(NbPopoverDirective) matrixPopup: QueryList<NbPopoverDirective>;

  // @ViewChild(NbPopoverDirective, { static: false }) popover: NbPopoverDirective;

  dm: DocMetrics;
  unitData = UnitData;
  unitOptions = UnitData.getShortOptions();
  issuerData = IssuerData;
  docConfig = DocConfig;
  compilationTypeData = CompilationTypeData;
  now = new Date();

  constructor(private api: ApiService, private roundPipe: RoundPipe, protected data: SharedDataService, private toastrService: NbToastrService) { }

  ngOnInit(): void {
    this.dm = new DocMetrics(this.api, this.compilation, this.lineItem, this.roundPipe, this.data);
  }

  save() {
    this.dm.save();
    this.recalc.emit();
    this.close.emit()
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.save();
    }
  }

  async transferPrice(lineItem: LineItemModel, recipient, unit, piecePrice, date) {
    const doctype = CompilationTypeData.get(this.compilation.type);
    const pricetype = DocConfig.get('prices').docs.indexOf(doctype.code);
    // let unit = lineItem.displayUnit;
    // let price = lineItem.discount > 0 ? lineItem.displayUnitPrice*(1-lineItem.discount/100) : lineItem.displayUnitPrice;
    const price = this.dm.metrics.convert(piecePrice, unit, 1)

    // console.log('=============== price to pricematrix ===================')
    // console.log('transferPrice issuer', this.compilation.issuer);
    // console.log('transferPrice recipient', recipient);
    // console.log('transferPrice product', mongoId(lineItem.finishedProduct));
    // console.log('transferPrice date', date);
    // console.log('transferPrice currency', this.compilation.currency);
    // console.log('transferPrice type', DocConfig.get('prices').price[pricetype]);
    // console.log('transferPrice unit', UnitData.get(unit).name);
    // console.log('transferPrice price', price);

    await this.api.savePrice(
      this.compilation.issuer,
      mongoId(recipient),
      mongoId(lineItem.finishedProduct),
      new Date(),
      this.compilation.etaCustomerDate ? new Date(this.compilation.etaCustomerDate) : undefined,
      this.compilation.currency,
      DocConfig.get('prices').price[pricetype] === 'PP' ? EAccountingType.Purchase : EAccountingType.Sale,
      +unit,
      +price.toFixed(6),
    );

    // this.reloadFinishedProductPrice(lineItem, price);
    this.toastrService.success(
      `price ${+price.toFixed(6)} ${this.compilation.currency} per ${UnitData.get(+unit).short} transferred`,
      'price matrix'
    );

    this.matrixPopup.forEach(p => p.hide());
  }

  closeMatrixPopup() {
      this.matrixPopup.forEach(p => p.hide()); // this.popover.hide();
  }

  async onPriceFileUpload(files: FileModel[], finishedProduct: FinishedProductModel, acc: EAccountingType) {
    this.api.updatePriceMatrixFiles(this.compilation.issuer, mongoId(this.compilation.recipient), mongoId(finishedProduct), acc, files);
  }

}
